import { el, ElemNode } from '@splice/elemaudio-core';
import { AssetCategorySlug, MidiAsset, SampleAsset } from './types';

export const kLabsMetadataVersion = 2;

// --------------------------------  Resources ---------------------------------
export type LabsResourceType = 'wav' | 'mid' | 'json';

export interface LabsResourceID<T extends LabsResourceType = LabsResourceType> {
  readonly value: string;
  readonly type: T;
}

export interface LabsResource<
  T_Type extends LabsResourceType = LabsResourceType,
  T_Metadata extends LabsFileMetadata = LabsFileMetadata,
> {
  readonly id: LabsResourceID<T_Type>;
  readonly metadata: T_Metadata;
  readonly file: File;
}

export type LabsAudioResource = LabsResource<'wav', LabsAudioResourceMetadata>;
export const isLabsAudioResource = (
  resource: LabsResource
): resource is LabsAudioResource => {
  return resource.id.type === 'wav';
};

export type LabsMidiResource = LabsResource<'mid', LabsMidiResourceMetadata>;
export const isLabsMidiResource = (
  resource: LabsResource
): resource is LabsMidiResource => {
  return resource.id.type === 'mid';
};

export type LabsJsonResource = LabsResource<'json', LabsFileMetadata>;
export const isLabsJsonResource = (
  resource: LabsResource
): resource is LabsJsonResource => {
  return resource.id.type === 'json';
};

// -----------------------------  Resource Metadata ----------------------------
/**
 * Very important! If any of these data structures change, the version number
 * above must be increased and a migration function added.
 * Optional fields are `null` rather than `undefined` so that they are included when serialized
 */

export interface LabsFileMetadata {
  version: number;
  originalFilename: string;
  filenameOnDisk: string;
  dateAdded: string;
}

export interface LabsBaseAssetMetadata {
  spliceUuid: string | null;
  bpm: number | null;
  chordType: string | null;
  key: string | null;
  artworkUrl: string | null;
  tags: string[];
}

export interface LabsAudioAssetMetadata extends LabsBaseAssetMetadata {
  type: AssetCategorySlug | null;
}

export interface LabsAudioMetadata extends LabsAudioAssetMetadata {
  duration_s: number;
  sampleRate: number;
  numberOfChannels: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LabsMidiMetadata extends LabsBaseAssetMetadata {}

// This is empty but here in case we add metadata for JSON resources
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LabsJsonMetadata {}

export type LabsResourceEditableMetadata =
  | LabsAudioAssetMetadata
  | LabsMidiMetadata
  | LabsJsonMetadata;

export type LabsInputMetadata<T> = T & {
  filename: string;
};

export type LabsInputAudioMetadata = LabsInputMetadata<LabsAudioAssetMetadata>;
export type LabsInputMidiMetadata = LabsInputMetadata<LabsMidiMetadata>;
export type LabsInputJsonMetadata = LabsInputMetadata<object>;

export interface LabsAudioResourceMetadata
  extends LabsFileMetadata,
    LabsAudioMetadata {}

export interface LabsMidiResourceMetadata
  extends LabsFileMetadata,
    LabsMidiMetadata {}

export type LabsJsonResourceMetadata = LabsFileMetadata;

export type LabsResourceMetadata =
  | LabsAudioResourceMetadata
  | LabsMidiResourceMetadata
  | LabsJsonResourceMetadata;

export type ParseAndMigrateMetadata<T extends LabsFileMetadata> = (
  parsed: unknown,
  filenameOnDisk: string
) => T | Error;

export const sanitizeFileName = (fileName: string) =>
  fileName.replace(/\//g, '_');

export const createBaseAssetMetadata = (
  inputFileName: string,
  assetWithArtworkUrl: Readonly<AssetWithArtworkUrl> | undefined
): LabsInputMetadata<LabsBaseAssetMetadata> => {
  const metadata: LabsInputMetadata<LabsBaseAssetMetadata> = {
    filename: sanitizeFileName(inputFileName),
    spliceUuid: assetWithArtworkUrl?.asset.uuid ?? null,
    bpm: assetWithArtworkUrl?.asset.bpm ?? null,
    artworkUrl: assetWithArtworkUrl?.artUrl ?? null,
    chordType: assetWithArtworkUrl?.asset.chord_type ?? null,
    key: assetWithArtworkUrl?.asset.key ?? null,
    tags: (assetWithArtworkUrl?.asset.tags ?? []).reduce<string[]>(
      (accum, tag) => {
        if (tag?.label !== null && tag?.label !== undefined) {
          accum.push(tag.label);
        }
        return accum;
      },
      []
    ),
  };
  return metadata;
};

// ----------------------------- Elementary Graphs -----------------------------

export type StereoNodes = [ElemNode, ElemNode];

export interface LabsElementaryGraph {
  nodes: StereoNodes;
  resourceIDs: Array<LabsResourceID<'wav'>>;
}

// ---------------------------- Elementary Metering ----------------------------
// Important! this must match elementary's definition, so don't change it
export interface ElementaryMeterEvent {
  source?: string | undefined;
  min: number;
  max: number;
}

export const kLabsMeterPrefix = 'labs-meter';

export type ElementaryMeterKey<T extends string> =
  `${typeof kLabsMeterPrefix}:${T}`;

export const createElementaryMeterKey = <T extends string>(
  id: T
): ElementaryMeterKey<T> => `${kLabsMeterPrefix}:${id}`;

export type ElementaryMeterName<T extends string> =
  `${ElementaryMeterKey<T>}:${string}`;

export const createElementaryMeterName = <T extends string, V extends string>(
  id: T,
  serializedValues: V
): ElementaryMeterName<T> =>
  `${createElementaryMeterKey(id)}:${serializedValues}`;

export interface LabsMeterEvent {
  prefix: typeof kLabsMeterPrefix;
  id: string;
  min: number;
  max: number;
  extra: string | undefined;
}

export const getLabsMeterEvent = (
  event: ElementaryMeterEvent
): LabsMeterEvent | undefined => {
  if (event.source === undefined) {
    return;
  }
  const firstColon = event.source.indexOf(':');
  if (firstColon === -1) {
    return;
  }
  const prefix = event.source.substring(0, firstColon);
  if (prefix !== kLabsMeterPrefix) {
    return;
  }
  const secondColon = event.source.indexOf(':', firstColon + 1);
  if (secondColon === -1) {
    return;
  }
  const id = event.source.substring(firstColon + 1, secondColon);
  if (id.length === 0) {
    return;
  }
  const extra = event.source.substring(secondColon + 1);
  return { prefix, id, min: event.min, max: event.max, extra };
};

export const createMeterNode = (
  id: string,
  values: string,
  node: ElemNode
): ElemNode => {
  const key = createElementaryMeterKey(id);
  const name = createElementaryMeterName(id, values);
  return el.mul(0, el.meter({ key, name }, node));
};

const kElementaryTransportUnits = ['seconds', 'beats'] as const;
export type ElementaryTransportUnit =
  (typeof kElementaryTransportUnits)[number];

export const kGlobalClockName = 'global-beat-clock';
export const kGlobalClockUnit: ElementaryTransportUnit = 'beats';
export const createGlobalClockData = (playCount: number): string =>
  `${playCount}.${kGlobalClockUnit}`;

export const createGlobalClockMeterNode = (
  playCount: number,
  ppqNode: ElemNode
): ElemNode => {
  return createMeterNode(
    kGlobalClockName,
    createGlobalClockData(playCount),
    ppqNode
  );
};

export interface ElementaryTransportPosition {
  playCount: number;
  value: number;
  unit: 'seconds' | 'beats';
}

export interface TimeSignature {
  numerator: number;
  denominator: number;
}

export interface ElementaryTransportStateBase {
  isPlaying: boolean;
  bpm: number;
  timeSignature: TimeSignature;
}

export interface ElementaryTransportState extends ElementaryTransportStateBase {
  position: ElementaryTransportPosition;
}

export const getGlobalBeatClockPosition = (
  event: LabsMeterEvent
): ElementaryTransportPosition | undefined => {
  if (event.id !== kGlobalClockName) {
    return;
  }
  if (event.extra === undefined) {
    return;
  }
  const parts = event.extra.split('.');
  if (parts.length !== 2) {
    return;
  }
  const playCount = parseInt(parts[0], 10);
  if (!Number.isInteger(playCount)) {
    return;
  }
  const unit = parts[1];
  if (unit !== kGlobalClockUnit) {
    return;
  }

  return {
    playCount,
    unit: kGlobalClockUnit,
    value: event.max,
  };
};

// ---------------------------- Messages to iframe -----------------------------
export enum LabsMessageToLiveType {
  AssetsDragStarted = 'AssetDragStarted',
  ResourceAdded = 'ResourceAdded',
  PortTransfer = 'PortTransfer',
  AllResources = 'AllResources',
  Refresh = 'Refresh',
  MidiEvent = 'MidiEvent',
  TransportState = 'TransportState',
  ElementaryEvent = 'ElementaryEvent',
  AuthToken = 'AuthToken',
  ResourceExported = 'ResourceExported',
  HostBottomInset = 'HostBottomInset',
}

export interface BaseMessageToLive {
  type: LabsMessageToLiveType;
}

export interface AssetWithArtworkUrl {
  asset: SampleAsset | MidiAsset;
  artUrl?: string;
}

type AssetFilename = string;
export type SampleAssetsWithArtworkUrl = Record<
  AssetFilename,
  AssetWithArtworkUrl
>;

export interface AllResourcesMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.AllResources;
  namespace: string;
  resources: LabsResource[];
}

/**
 * What the host did to the audio before handing it over, as the values the
 * delivered file ends up with rather than offsets to re-apply. With Transpose
 * & Warp on, the file dragged over is a re-render at the session tempo and key,
 * so the catalog asset above no longer describes it.
 *
 * Every field is optional in both directions: absent means "that dimension was
 * left alone, the catalog value still holds", and a host that predates this
 * field sends nothing at all — the embedded app falls back to reading the
 * render suffixes off the filename.
 */
export interface DraggedAssetTransform {
  /** Tempo of the delivered audio. Host tempo, times any timestretch multiplier. */
  bpm?: number;
  /**
   * Key of the delivered audio. Absent for a pitch shift whose landing key the
   * host could not work out, which means unknown rather than unchanged — read
   * it together with `pitchSemitones`.
   */
  key?: string;
  /** Semitones of pitch shift applied, for display alongside the values. */
  pitchSemitones?: number;
  /** Timestretch multiplier applied on top of the host tempo (`2` = double-time). */
  timestretchMultiplier?: number;
}

export interface AssetsDragStartedMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.AssetsDragStarted;
  assets: AssetWithArtworkUrl[];
  /**
   * Present when the host is delivering a re-rendered copy. Hosts that don't
   * transform on drag, and older hosts, omit it.
   */
  transform?: DraggedAssetTransform;
}

export interface TransportStateMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.TransportState;
  state: ElementaryTransportState;
}

export interface MidiEventMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.MidiEvent;
  noteNumber: number;
  velocity: number;
}

export interface PortTransferMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.PortTransfer;
}

export interface RefreshMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.Refresh;
}

export interface ResourceAddedMessage extends BaseMessageToLive {
  type: LabsMessageToLiveType.ResourceAdded;
  liveID: number;
  resource: LabsResource<LabsResourceType, LabsFileMetadata>;
}

export interface MeterEventMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.ElementaryEvent;
  event: LabsMeterEvent;
}

export interface AuthTokenMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.AuthToken;
  accessToken: string;
  expiresAt: number;
}

/**
 * Sidepanel hosts only: confirms an `ExportResource` file was written to disk
 * host-side, unlocking reveal-on-disk (and native drag-out) for the resource.
 */
export interface ResourceExportedMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.ResourceExported;
  id: LabsResourceID;
}

/**
 * Sidepanel hosts only: how many px of floating host UI (the Sounds plugin's
 * playbar pill) currently cover the bottom of the embedded viewport — 0 when
 * nothing floats. The client adds it to its scroll containers' bottom padding
 * so the last rows can scroll clear of the pill.
 */
export interface HostBottomInsetMessageToLive extends BaseMessageToLive {
  type: LabsMessageToLiveType.HostBottomInset;
  px: number;
}

export type LabsMessageToLive =
  | AllResourcesMessageToLive
  | AssetsDragStartedMessageToLive
  | TransportStateMessageToLive
  | MidiEventMessageToLive
  | PortTransferMessageToLive
  | RefreshMessageToLive
  | ResourceAddedMessage
  | MeterEventMessageToLive
  | AuthTokenMessageToLive
  | ResourceExportedMessageToLive
  | HostBottomInsetMessageToLive;

// ------------------------- Messages to Platform ------------------------------
export enum LabsMessageToPlatformType {
  AddFiles = 'AddFiles',
  ClearElementaryResourceCache = 'ClearElementaryResourceCache',
  DeleteResources = 'DeleteResources',
  ElementaryGraph = 'ElementaryGraph',
  GetAllResources = 'GetAllResources',
  GetTransportState = 'GetTransportState',
  OpenExternalLink = 'OpenExternalLink',
  PageChange = 'PageChange',
  RevealResourceOnDisk = 'RevealResourceOnDisk',
  UpdateResourceMetadata = 'UpdateResourceMetadata',
  RequestAuthToken = 'RequestAuthToken',
  ExportResource = 'ExportResource',
  StartNativeDrag = 'StartNativeDrag',
}

export interface LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType;
}

interface LabsInputFileBase {
  type: LabsResourceType;
  file: File;
  liveID: number;
}

export interface LabsInputAudioFile extends LabsInputFileBase {
  type: 'wav';
  metadata: Readonly<LabsInputAudioMetadata>;
}

export interface LabsInputMidiFile extends LabsInputFileBase {
  type: 'mid';
  metadata: Readonly<LabsInputMidiMetadata>;
}

export interface LabsInputJsonFile extends LabsInputFileBase {
  type: 'json';
}

export type LabsInputFile =
  | LabsInputAudioFile
  | LabsInputMidiFile
  | LabsInputJsonFile;

export interface AddFilesMessageToPlatform extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.AddFiles;
  namespace: string;
  files: LabsInputFile[];
}

export interface ClearElementaryResourceCacheMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.ClearElementaryResourceCache;
}

export interface DeleteResourcesMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.DeleteResources;
  namespace: string;
  ids: readonly LabsResourceID[];
}

export interface ElementaryGraphMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.ElementaryGraph;
  namespace: string;
  graph: Readonly<LabsElementaryGraph>;
}

export interface GetAllResourcesMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.GetAllResources;
  namespace: string;
}

export interface GetTransportStateMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.GetTransportState;
}

export interface OpenExternalLinkMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.OpenExternalLink;
  url: string;
}

export interface PageChangeMessageToPlatform extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.PageChange;
  pathname: string;
}

export interface RevealResourceOnDiskMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.RevealResourceOnDisk;
  id: LabsResourceID;
}

export interface UpdateAssetMetadataMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.UpdateResourceMetadata;
  namespace: string;
  id: LabsResourceID;
  metadata: LabsResourceEditableMetadata;
}

export interface RequestAuthTokenMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.RequestAuthToken;
}

/**
 * Sidepanel hosts only: asks the host to persist a client-side resource to
 * disk so it can be revealed (and later natively dragged) into the DAW. The
 * host answers with `ResourceExported`. Not used by the labs plugin, where
 * resources are host-owned from the start (`AddFiles`).
 */
export interface ExportResourceMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.ExportResource;
  id: LabsResourceID;
  file: File;
}

/**
 * Sidepanel hosts only: sent on `dragstart` of a result row (with the HTML5
 * drag cancelled) so the host starts a native OS drag with the previously
 * exported file — dropping it straight into the DAW.
 */
export interface StartNativeDragMessageToPlatform
  extends LabsBaseMessageToPlatform {
  type: LabsMessageToPlatformType.StartNativeDrag;
  id: LabsResourceID;
}

export type LabsMessageToPlatform =
  | AddFilesMessageToPlatform
  | ClearElementaryResourceCacheMessageToPlatform
  | DeleteResourcesMessageToPlatform
  | ElementaryGraphMessageToPlatform
  | GetAllResourcesMessageToPlatform
  | GetTransportStateMessageToPlatform
  | OpenExternalLinkMessageToPlatform
  | PageChangeMessageToPlatform
  | RevealResourceOnDiskMessageToPlatform
  | UpdateAssetMetadataMessageToPlatform
  | RequestAuthTokenMessageToPlatform
  | ExportResourceMessageToPlatform
  | StartNativeDragMessageToPlatform;
